<template>
  <content-dialog
    v-model="dialogOpen"
    fullscreen-mobile
    width="650px"
    min-height="200px"
    title="Licence Details"
    without-blur
    :toolbar-classes="{'text-white': true, 'bg-blue-8': !isArchived, 'bg-red-8': isArchived}"
    :loading="loading"
    :tags="[helpCodes.licences]"
    @hide="$emit('close')"
  >
    <template #toolbar>
      <q-toolbar>
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="mdi-close"
        />
        <page-toolbar-title>
          <template v-if="isArchived || (!loading && !canEditLicence)" #subtitle>
            {{ concatStrIf(' | ', isArchived && 'Archived', !canEditLicence || isArchived && 'Read-only') }}
          </template>
          Licence
        </page-toolbar-title>
        <help-button />
      </q-toolbar>
    </template>

    <div v-if="!loading && licence !== null">
      <q-list dense>
        <q-item-label
          header
          class="text-subtitle2"
        >
          Details
        </q-item-label>

        <detail-item
          v-if="licence && licence.user"
          name="Staff Member"
          :clickable="clickableStaffMember"
          :view-more="clickableStaffMember"
          :value="licence.user.name"
          @click="viewingStaffID = licence.user.id"
        >
          <template #default>
            <q-item-label>
              {{ licence.user.name }}
            </q-item-label>
            <q-item-label
              v-if="licence.user.deactivated"
              caption
              class="text-bold text-red-7"
            >
              Deactivated
            </q-item-label>
          </template>

          <template #icon>
            <profile-image
              :profile-image="licence.user.profile_image"
              :initials="licence.user.initials"
              :color="licence.user.color"
              size="sm"
            />
          </template>
        </detail-item>

        <detail-item
          icon="mdi-card-account-details"
          name="Licence Number"
          :value="licence.licence_number"
        />

        <detail-item
          v-if="licence.descriptor"
          icon="mdi-text-short"
          name="Title / Descriptor"
          :value="licence.descriptor"
        />

        <detail-item
          icon="mdi-map-marker"
          name="Location"
        >
          {{ licence.administrative_area }}, {{ licence.country }}
        </detail-item>

        <detail-item
          v-show="licence.expiry_date"
          icon="mdi-calendar-remove"
          name="Expiry Date"
        >
          <q-item-label>{{ $formatDate(licence.expiry_date, luxonFormats.FRIENDLY_DATE) }}</q-item-label>
          <q-item-label
            v-if="licence.is_set_to_expire"
            class="text-bold text-red-7 q-mb-xs"
            caption
          >
            {{ licence.is_set_to_expire }}
          </q-item-label>
        </detail-item>

        <detail-item
          icon="mdi-clipboard-file-outline"
          name="Notes & Files"
          with-input
          sticky
          :sticky-top="50"
        >
          <simple-timeline
            :timeline="timeline"
            :read-only="!canEditLicence || isArchived"
            :hide-no-entries-text="canEditLicence && !isArchived"
            disable-pinning
            simple-note
            @add-note="(note, done) => baseAddNote(note, done, licence.id, timeline)"
            @update-note="(note, done) => baseUpdateNote(note, done)"
            @remove-note="(entryId, reference) => baseRemoveNote(entryId, reference)"
            @save-attachments="(keepIds, removeObjs, done) => baseSaveAttachments(keepIds, removeObjs, done, licence.id, timeline)"
            @remove-attachment="(entryId, key, reference) => baseRemoveAttachment(entryId, key, licence.id, reference)"
          />
        </detail-item>
      </q-list>
    </div>
    <div v-else-if="!loading">
      <no-results
        icon="mdi-account-alert"
        title="Licence not found"
      />
    </div>

    <template #bottom>
      <q-toolbar v-if="licence && canEditLicence">
        <q-space />
        <q-btn
          :icon-right="isArchived ? 'mdi-package-up' : 'mdi-package-down'"
          :color="isArchived ? 'green' : 'red'"
          :label="isArchived ? 'Restore' : 'Archive'"
          flat
          @click="archiveLicence"
        />
        <q-btn
          v-if="!isArchived"
          label="Edit"
          icon-right="mdi-pencil"
          color="blue-grey"
          @click="$refs.editDialog.open()"
        />
      </q-toolbar>
    </template>
  </content-dialog>

  <EditDialog
    v-if="licence"
    ref="editDialog"
    :licence="licence"
    @saved="load(true)"
  />
  <ViewStaffDialog
    :id="viewingStaffID"
    allow-editing
    @updated="load"
    @close="viewingStaffID = null"
  />
</template>

<script>
import { defineAsyncComponent } from 'vue'
import { concatStrIf } from 'src/helpers/globals'

import licenceTimelineMethodsMixin from 'components/app/staff/licences/partials/licenceTimelineMethodsMixin'
import EditDialog from './EditDialog.vue'

export default {
  components: {
    ViewStaffDialog: defineAsyncComponent(() => import('../../partials/view/Dialog.vue')),
    EditDialog
  },
  mixins: [licenceTimelineMethodsMixin],
  props: {
    id: {
      type: Number,
      default: null
    },
    clickableStaffMember: {
      type: Boolean,
      default: true
    }
  },
  emits: ['close', 'updated', 'archived'],
  data () {
    return {
      dialogOpen: false,
      loading: false,
      licence: null,
      timeline: [],
      viewingStaffID: null
    }
  },
  computed: {
    canEditLicence () {
      return this.licence && this.licence.can_edit
    },
    isArchived () {
      return this.licence && this.licence.archived
    }
  },
  watch: {
    'id' (val) {
      if (val) {
        this.dialogOpen = true
        this.load()
      }
      else {
        this.dialogOpen = false
        this.licence = null
        this.timeline = []
      }
    }
  },
  methods: {
    concatStrIf,
    async load (savedChanges = false) {
      if (this.id === null) { return }
      this.dialogOpen = true
      this.loading = true
      this.licence = null
      this.timeline = []
      const { status, payload } = await this.$get('v1/business/company/licences', {
        licence_id: this.id
      })
      this.loading = false
      if (status === 'success') {
        this.licence = payload.licence
        this.timeline = payload.timeline
        if (savedChanges) {
          this.$emit('updated', payload.licence)
        }
      }
    },
    archiveLicence () {
      this.$q.dialog({
        title: 'Confirm',
        message: [
          `Are you sure you want to ${this.isArchived ? 'restore' : 'archive'} this licence?`,
          !this.isArchived && 'This licence may not remain visible on some existing reports.'
        ],
        ok: true,
        cancel: true,
        color: 'orange-7'
      })
        .onOk(async () => {
          this.$loading.show()
          const { status } = await this.$remove('v1/business/company/licences', {
            licence_id: this.licence.id
          })
          this.$loading.hide()
          if (status === 'success') {
            this.$bus.emit('licences:reload', true, true)
            if (!this.isArchived) {
              this.$q.notify({
                color: 'orange-7',
                textColor: 'black',
                message: 'Licence successfully archived'
              })
            }
            else {
              this.$q.notify({
                color: 'green',
                message: 'Licence successfully restored'
              })
            }
            this.licence.archived = !this.licence.archived
            this.$emit('archived', this.licence.archived)
          }
        })
    }
  },
  mounted () {
    this.load()
  }
}
</script>
